import * as THREE from 'three';
import { localHeadline } from '../content/headlines';
import type { Choice } from '../content/types';
import { hourOf } from '../state/rules';
import type { Interactions } from '../systems/Interactions';
import { SPOTS, type Vec2 } from '../world/layout';
import { box, mat, mesh } from '../world/materials';
import type { Activities } from './Activities';
import type { GameServices } from './services';

interface Seat extends Vec2 {
  /** Which way you face sitting there. */
  facing: number;
  height: number;
}

interface Fire {
  id: string;
  name: string;
  /** Where the fire is (and how high the flames sit). */
  at: Vec2;
  y: number;
  seat: Seat;
  /** Somebody who's usually around at night and has stories. */
  host?: string;
  flames: THREE.Group;
  /** Always burning (the burn barrels in the Back Forty), or just evenings. */
  always?: boolean;
}

const STORIES = [
  'The one about the possum that got into the deep fryer at the church fish fry. Nobody ate for a week. Then everybody did.',
  'The night the Hendersons tried to jump the crick in a riding mower. The mower made it. Mr. Henderson did not, but he lived.',
  'A ghost story about Lot 13. Halfway through, somebody hears the rocking chair. Everybody leaves the fire at once.',
  'The time Darlene chased an Apex man off the lot with a weed eater. "Still had the price tag on it," somebody adds. "The weed eater."',
  "A long one about a 1997 Monte Carlo that was 'basically a race car.' It was not a race car.",
  'Somebody swears they saw lights over the Back Forty last week. Real late. Everybody laughs. Then nobody does.',
];

const HOST_STORIES: Record<string, string[]> = {
  earl: [
    "Earl tells you about the brisket that won Tri-County in '04. He gets choked up. It was a good brisket.",
    'Earl explains the three rules of smoking meat. You lose count at seven.',
  ],
  pete: [
    'Possum Pete tells you about the time he lived in a hollow tree for a summer. "Best rent I ever paid."',
    'Pete says the crick talks at night. You listen. It does sound like it says "Pete."',
  ],
  terry: [
    "Trenchcoat Terry opens his coat to show you his wares. It's all hot dogs. 'Inventory,' he says.",
    "Terry tells you a 'business secret.' You promise not to tell. You couldn't if you tried; it made no sense.",
  ],
};

function flames(scale = 1): THREE.Group {
  const g = new THREE.Group();
  const hot = new THREE.MeshStandardMaterial({
    color: '#ffb347',
    emissive: new THREE.Color('#ff7a1a'),
    emissiveIntensity: 2,
    transparent: true,
    opacity: 0.88,
  });
  const core = new THREE.MeshStandardMaterial({
    color: '#fff2b0',
    emissive: new THREE.Color('#ffd27a'),
    emissiveIntensity: 2.4,
    transparent: true,
    opacity: 0.9,
  });
  for (const [x, z, h, m] of [
    [0, 0, 0.75, hot],
    [0.15, 0.08, 0.5, hot],
    [-0.14, -0.06, 0.55, hot],
    [0, 0, 0.4, core],
  ] as const) {
    const c = mesh(new THREE.ConeGeometry(0.16 + h * 0.1, h, 7), m, x, h / 2, z, { cast: false });
    g.add(c);
  }
  g.add(
    mesh(
      new THREE.CylinderGeometry(0.42, 0.45, 0.06, 10),
      mat('#3a1a0a', { emissive: '#ff4a0a', emissiveIntensity: 1.2 }),
      0,
      0.03,
      0,
      { cast: false },
    ),
  );
  g.scale.setScalar(scale);
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}

/**
 * Fire pits and couches: Earl's backyard fire, the one at the Commons, Possum Pete's,
 * the burn barrels in the Back Forty and the curb couch by the BMX field. Sit a spell,
 * roast a weenie, hear a story, and every so often set the couch on fire.
 */
export class Campfires {
  private readonly fires: Fire[] = [];
  /** The curb couch burning: when it started (-1 when it's not). */
  private burning = -1;
  private burned = false;
  private readonly charred: THREE.Group;
  private readonly bigFire: THREE.Group;
  private spark = 0;

  constructor(
    private readonly s: GameServices,
    private readonly act: Activities,
  ) {
    const { world } = s;
    const ring = (center: Vec2, i: number, n: number, offset: number, r: number): Seat => {
      const a = (i / n) * Math.PI * 2 + offset;
      return {
        x: center.x + Math.cos(a) * r,
        z: center.z + Math.sin(a) * r,
        facing: Math.atan2(-Math.cos(a), -Math.sin(a)),
        height: 0.42,
      };
    };
    const earl = SPOTS.earlFirePit;
    const commons = SPOTS.commonsFirePit;
    const pete = { x: SPOTS.pete.x - 1.5, z: SPOTS.pete.z + 2.5 };
    const bmx = SPOTS.bmxField;
    const barrel = { x: bmx.x + 2.2, z: bmx.z - 5.2 };
    const couch = { x: bmx.x, z: bmx.z - 6 };
    const camp = SPOTS.barrelCamp;
    const add = (f: Omit<Fire, 'flames'>, scale: number): void => {
      const fl = flames(scale);
      fl.position.set(f.at.x, f.y, f.at.z);
      fl.visible = false;
      world.dynamic.add(fl);
      this.fires.push({ ...f, flames: fl });
    };
    add(
      {
        id: 'earl',
        name: "Earl's fire pit",
        at: earl,
        y: 0.05,
        seat: ring(earl, 0, 4, 0.4, 1.7),
        host: 'earl',
      },
      1,
    );
    add(
      {
        id: 'commons',
        name: 'the Commons fire pit',
        at: commons,
        y: 0.05,
        seat: ring(commons, 0, 3, 0.6, 1.8),
      },
      1,
    );
    add(
      {
        id: 'pete',
        name: "Possum Pete's fire",
        at: pete,
        y: 0.05,
        seat: { x: SPOTS.pete.x - 3.4, z: SPOTS.pete.z + 2, facing: 1.2, height: 0.42 },
        host: 'pete',
      },
      0.9,
    );
    add(
      {
        id: 'bmx',
        name: 'the curb couch',
        at: barrel,
        y: 0.9,
        seat: { x: couch.x, z: couch.z + 0.1, facing: 0.1, height: 0.5 },
      },
      0.55,
    );
    add(
      {
        id: 'camp',
        name: 'the burn barrel couch',
        at: { x: camp.x, z: camp.z },
        y: 0.9,
        seat: { x: camp.x - 1, z: camp.z - 2.9, facing: Math.PI, height: 0.5 },
        host: 'terry',
        always: true,
      },
      0.01,
    );
    // What's left of the couch after, and the bonfire while it goes.
    this.charred = new THREE.Group();
    const char = mat('#1b1612', { rough: 1 });
    this.charred.add(box(2.0, 0.12, 0.85, char, 0, 0.25, 0));
    for (const x of [-0.9, 0.9]) this.charred.add(box(0.12, 0.55, 0.8, char, x, 0.35, 0));
    for (let i = 0; i < 5; i++)
      this.charred.add(
        box(0.04, 0.3, 0.04, mat('#5a5a5a', { metal: 0.6 }), -0.7 + i * 0.35, 0.5, -0.2 + (i % 2) * 0.3),
      );
    this.charred.position.set(couch.x, 0, couch.z);
    this.charred.rotation.y = 0.1;
    this.charred.visible = false;
    world.dynamic.add(this.charred);
    this.bigFire = flames(2.6);
    this.bigFire.position.set(couch.x, 0.2, couch.z);
    this.bigFire.visible = false;
    world.dynamic.add(this.bigFire);
  }

  private lit(f: Fire): boolean {
    if (f.always) return true;
    const h = hourOf(this.s.store.minutes);
    return h >= 18 || h < 2;
  }

  register(interactions: Interactions): void {
    for (const f of this.fires) {
      interactions.add({
        id: `fire-${f.id}`,
        at: () => f.seat,
        radius: 1.6,
        prompt: () =>
          f.id === 'bmx' && this.burned
            ? null
            : f.id === 'bmx' || f.id === 'camp'
              ? `Sit on ${f.name}`
              : this.lit(f)
                ? `Sit by ${f.name}`
                : `${f.name[0]?.toUpperCase()}${f.name.slice(1)} (lit after 6 PM)`,
        action: () => this.sit(f),
        icon: f.id === 'bmx' || f.id === 'camp' ? ':couch:' : ':fire:',
      });
    }
  }

  private sit(f: Fire): void {
    const { player, store, ui } = this.s;
    if (!this.act.canAct()) return;
    const lit = this.lit(f);
    if (!lit && f.id !== 'bmx') {
      store.toast(
        "The fire's out. It's a ring of rocks and a Busch Light can. Come back after dark.",
        'info',
        ':fire:',
      );
      return;
    }
    const seat = f.seat;
    player.teleport(seat.x, seat.z, seat.facing);
    player.doBusy({ pose: 'sit', seat: seat.height, until: Infinity, facing: seat.facing });
    const standUp = (): void => {
      player.busy = null;
      player.teleport(
        seat.x + Math.sin(seat.facing) * 0.9,
        seat.z + Math.cos(seat.facing) * 0.9,
        seat.facing,
      );
    };
    const choices: Choice[] = [
      {
        label: lit ? 'Stare into the fire a while' : 'Kick back a while',
        run: ({ store: st }) => {
          const first = !st.doneToday(`fire_${f.id}`);
          st.markToday(`fire_${f.id}`);
          if (first) st.incr('campfires');
          return {
            text: this.story(f, lit),
            stats: { energy: first ? 12 : 4 },
            clout: first && lit ? 2 : 0,
            minutes: 30,
          };
        },
      },
      {
        label: 'Crack a cold one',
        hint: '-1 :beer:',
        requires: ({ store: st }) => st.has('beer') || 'No beer',
        run: ({ store: st, sfx }) => {
          st.take('beer');
          st.incr('beers');
          st.give('cans', 1);
          sfx('canOpen');
          return {
            text: 'The can hisses. The fire pops. Somewhere a dog barks at nothing. Perfect.',
            stats: { buzz: 16, energy: 3 },
            minutes: 10,
          };
        },
      },
    ];
    if (lit && f.id !== 'bmx')
      choices.push({
        label: 'Roast a weenie over the fire',
        hint: '-1 :hotdog:',
        requires: ({ store: st }) => st.has('hotdog') || 'Need a hot dog (Gas-N-Grub)',
        run: ({ store: st, sfx }) => {
          st.take('hotdog');
          st.incr('meals');
          sfx('crunch');
          return {
            text: 'On a coat hanger, over the coals, till it splits and the ends go black. Better than any roller grill ever made.',
            stats: { hunger: 50, health: 4 },
            minutes: 15,
          };
        },
      });
    if (f.id === 'bmx') {
      const night = hourOf(store.minutes) >= 20 || hourOf(store.minutes) < 4;
      choices.push({
        label: 'Set the couch on fire',
        hint: night ? 'needs a lighter (:smokes:) or :fireworks:' : 'after dark',
        requires: ({ store: st }) =>
          !night
            ? 'Not in broad daylight. Have some class.'
            : st.doneToday('couch_fire')
              ? "There's no couch left to burn."
              : st.has('cigs') || st.has('fireworks') || 'Need a lighter (buy smokes) or fireworks',
        run: () => {
          window.setTimeout(() => this.lightCouch(), 0);
        },
      });
    }
    ui.modal.show(
      {
        title: f.name[0]?.toUpperCase() + f.name.slice(1),
        art: f.id === 'bmx' || f.id === 'camp' ? ':couch:' : ':fire:',
        text: lit
          ? 'You pull up a seat. The fire crackles. For a minute, the rent can wait.'
          : 'A couch on the curb. Sun-bleached, a little damp, perfect.',
        choices,
        leave: 'Get up',
      },
      standUp,
    );
  }

  private story(f: Fire, lit: boolean): string {
    const { rng, npcs, player } = this.s;
    if (!lit)
      return rng.pick([
        'You sit. The couch sighs. So do you.',
        'A kid on a BMX does a sick jump. You give him a thumbs up. He does not see.',
      ]);
    const host = f.host ? npcs.get(f.host) : undefined;
    if (host?.active && host.position.distanceTo(player.position) < 25) {
      const lines = HOST_STORIES[f.host ?? ''] ?? STORIES;
      return rng.pick(lines);
    }
    const story = rng.pick(STORIES);
    return `Somebody wanders over from the dark and tells ${story.charAt(0).toLowerCase()}${story.slice(1)}`;
  }

  /** The Friday-night-any-night tradition. */
  private lightCouch(): void {
    const { store, rng, player } = this.s;
    if (store.doneToday('couch_fire')) return;
    if (!store.take('fireworks')) store.take('cigs');
    store.markToday('couch_fire');
    store.incr('couchFires');
    store.unlock('couch_fire');
    this.burning = this.s.now();
    this.burned = true;
    player.busy = null;
    const seat = this.fires.find((x) => x.id === 'bmx')?.seat;
    if (seat) player.teleport(seat.x, seat.z + 3, Math.PI);
    this.s.sfx('boom');
    store.apply({ clout: 12 });
    store.headline(localHeadline('couchfire', store.state.player.name));
    store.toast(
      'WHOOMP. That couch had a lot of secrets in it, and they are all on fire now.',
      'meme',
      ':fire:',
    );
    if (rng.chance(0.3))
      window.setTimeout(() => {
        this.s.ctx.world.cops();
        store.toast('Somebody called it in. Here comes the county.', 'bad', ':police-car:');
      }, 6000);
  }

  /** Per frame: flames flicker, the couch burns down. */
  update(time: number, dt: number): void {
    const { world, store } = this.s;
    for (const f of this.fires) {
      const on = f.id !== 'camp' && this.lit(f) && !(f.id === 'bmx' && this.burning >= 0);
      if (f.flames.visible !== on) f.flames.visible = on;
      if (!on) continue;
      f.flames.children.forEach((c, i) => {
        c.scale.y = 1 + Math.sin(time * (9 + i * 3) + i) * 0.18;
        c.rotation.y = time * (0.5 + i * 0.3);
      });
    }
    world.fireBoost = 0;
    if (this.burning >= 0) {
      const t = this.s.now() - this.burning;
      const fire = this.bigFire;
      world.curbCouch.visible = t < 2;
      this.charred.visible = t >= 2;
      fire.visible = t < 50;
      fire.scale.setScalar(2.6 * Math.min(1, t / 1.5) * Math.max(0.2, 1 - Math.max(0, t - 30) / 20));
      fire.children.forEach((c, i) => (c.scale.y = 1 + Math.sin(time * (11 + i * 3)) * 0.25));
      world.fireBoost = fire.visible ? (35 * fire.scale.x) / 2.6 : 0;
      this.spark -= dt;
      if (fire.visible && this.spark <= 0) {
        this.spark = 0.05;
        const p = fire.position;
        world.sparks.spawn({
          x: p.x + (Math.random() - 0.5) * 1.5,
          y: 1,
          z: p.z + (Math.random() - 0.5) * 0.8,
          vx: (Math.random() - 0.5) * 2,
          vy: 3 + Math.random() * 3,
          vz: (Math.random() - 0.5) * 2,
          life: 1.2,
          size: 0.12,
          color: '#ffb347',
          gravity: -1,
        });
        world.smoke.spawn({
          x: p.x,
          y: 2,
          z: p.z,
          vx: (Math.random() - 0.5) * 0.5,
          vy: 1.6,
          life: 3,
          size: 0.8,
          grow: 3,
          color: '#3a3430',
        });
      }
      if (t > 50) this.burning = -1;
    }
    // A new couch shows up on the curb overnight. Nobody knows who brings them.
    if (this.burned && this.burning < 0 && !store.doneToday('couch_fire')) {
      this.burned = false;
      world.curbCouch.visible = true;
      this.charred.visible = false;
    }
  }
}
